// components/Apps/Quiz/Quiz.js
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Button } from '../../Atoms';
import QuizMeta from './QuizMeta';
import QuizQuestion from './QuizQuestion';
import QuizAnswerOptions from './QuizAnswerOptions';

const Quiz = ({ value }) => {
  const {
    quizTitle,
    quizDescription,
    instructions,
    quizAnswerType,
    booleanSettings,
    scaleSettings,
    dualScaleSettings,
    multipleChoiceSettings,
    questions = [],
    showScore,
    resultLow,
    resultMedium,
    resultHigh,
    collectUserName,
  } = value;

  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers]       = useState({});
  const [userName, setUserName]     = useState('');
  const [showResults, setShowResults]   = useState(false);
  const [showNameInput, setShowNameInput] = useState(collectUserName);

  const currentQuestion = questions[currentQuestionIndex];
  const hasAnswered = answers[currentQuestionIndex] !== undefined;

  const handleAnswer = (answer) => {
    setAnswers({ ...answers, [currentQuestionIndex]: answer });
  };

  const handleNext = () => {
    if (currentQuestionIndex === questions.length - 1) {
      setShowResults(true);
    } else {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  const handleRestart = () => {
    setCurrentQuestionIndex(0);
    setAnswers({});
    setShowResults(false);
    setShowNameInput(collectUserName);
  };

  const calculateScore = () => {
    const answeredCount = Object.keys(answers).length;
    if (answeredCount === 0) return 0;
    return Math.round((answeredCount / questions.length) * 100);
  };

  const getResultMessage = () => {
    const score = calculateScore();
    if (score < 33) return resultLow    || 'You can improve!';
    if (score < 66) return resultMedium || 'Good job!';
    return resultHigh || 'Excellent!';
  };

  // Name input screen
  if (showNameInput) {
    return (
      <div className="quiz">
        <div className="quiz__name-input">
          <div className="text-section-title">{quizTitle}</div>
          {quizDescription && (
            <div className="quiz__description">{quizDescription}</div>
          )}
          <input
            type="text"
            placeholder="Enter your name"
            value={userName}
            onChange={(e) => setUserName(e.target.value)}
            className="quiz__name-field"
          />
          <Button
            onClick={() => setShowNameInput(false)}
            disabled={!userName.trim()}
          >
            Start Quiz
          </Button>
        </div>
      </div>
    );
  }

  // Results screen
  if (showResults) {
    return (
      <div className="quiz">
        <div className="quiz__results">
          {userName && (
            <div className="text-section-title">Thanks, {userName}!</div>
          )}
          <div className="text-block-title">Quiz Complete!</div>
          {showScore && (
            <div className="stack stack-tight">
              <div className="quiz__score-value">{calculateScore()}%</div>
              <div className="quiz__score-message">{getResultMessage()}</div>
            </div>
          )}
          <div className="cluster cluster-center">
            <Button onClick={handleRestart}>Restart Quiz</Button>
          </div>
        </div>
      </div>
    );
  }

  // Quiz questions
  return (
    <div className="quiz">
      <QuizMeta
        quizTitle={quizTitle}
        quizDescription={quizDescription}
        instructions={instructions}
      />

      <QuizQuestion
        questionText={currentQuestion?.questionText}
        questionImage={currentQuestion?.questionImage}
      >
        <QuizAnswerOptions
          answerType={quizAnswerType}
          booleanSettings={booleanSettings}
          scaleSettings={scaleSettings}
          dualScaleSettings={dualScaleSettings}
          multipleChoiceSettings={multipleChoiceSettings}
          selectedAnswer={answers[currentQuestionIndex]}
          onAnswer={handleAnswer}
        />
      </QuizQuestion>

      <div className="quiz__navigation">
        <Button
          onClick={handlePrevious}
          disabled={currentQuestionIndex === 0}
          variant="ghost"
        >
          Previous
        </Button>
        <Button
          onClick={handleNext}
          disabled={!hasAnswered}
        >
          {currentQuestionIndex === questions.length - 1 ? 'Finish' : 'Next'}
        </Button>
      </div>
    </div>
  );
};

Quiz.propTypes = {
  value: PropTypes.shape({
    quizTitle:      PropTypes.string.isRequired,
    quizDescription: PropTypes.string,
    instructions:   PropTypes.string,
    quizAnswerType: PropTypes.oneOf(['boolean', 'scale', 'dual_scale', 'multiple_choice']).isRequired,
    booleanSettings: PropTypes.shape({
      optionA: PropTypes.string,
      optionB: PropTypes.string,
    }),
    scaleSettings: PropTypes.shape({
      scaleLabelLow:  PropTypes.string,
      scaleLabelHigh: PropTypes.string,
    }),
    dualScaleSettings: PropTypes.shape({
      leftLabel:  PropTypes.string,
      rightLabel: PropTypes.string,
    }),
    multipleChoiceSettings: PropTypes.shape({
      choices:               PropTypes.arrayOf(PropTypes.string),
      allowMultipleSelection: PropTypes.bool,
    }),
    questions: PropTypes.arrayOf(
      PropTypes.shape({
        questionText:  PropTypes.string.isRequired,
        questionImage: PropTypes.string,
      })
    ).isRequired,
    showScore:       PropTypes.bool,
    resultLow:       PropTypes.string,
    resultMedium:    PropTypes.string,
    resultHigh:      PropTypes.string,
    collectUserName: PropTypes.bool,
  }).isRequired,
};

export default Quiz;